// Configuración de Cloudinary mediante variables de entorno Vite
const CLOUDINARY_CLOUD_NAME = import.meta.env.VITE_CLOUDINARY_CLOUD_NAME;
const CLOUDINARY_UPLOAD_PRESET = import.meta.env.VITE_CLOUDINARY_UPLOAD_PRESET;

export interface CloudinaryResponse {
  public_id: string;
  secure_url: string;
  width: number;
  height: number;
  format: string;
  resource_type: string;
}

export interface UploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

export const uploadImageToCloudinary = async (
  file: File,
  onProgress?: (progress: UploadProgress) => void,
): Promise<CloudinaryResponse> => {
  if (!CLOUDINARY_CLOUD_NAME || !CLOUDINARY_UPLOAD_PRESET) {
    throw new Error(
      "Configuración de Cloudinary incompleta. Verifica VITE_CLOUDINARY_CLOUD_NAME y VITE_CLOUDINARY_UPLOAD_PRESET",
    );
  }

  const formData = new FormData();
  formData.append("file", file);
  formData.append("upload_preset", CLOUDINARY_UPLOAD_PRESET);
  formData.append("cloud_name", CLOUDINARY_CLOUD_NAME);

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener("progress", (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress({
          loaded: event.loaded,
          total: event.total,
          percentage: Math.round((event.loaded / event.total) * 100),
        });
      }
    });

    xhr.addEventListener("load", () => {
      if (xhr.status === 200) {
        try {
          const response: CloudinaryResponse = JSON.parse(xhr.responseText);
          resolve(response);
        } catch {
          reject(new Error("Error al procesar la respuesta de Cloudinary"));
        }
      } else {
        reject(new Error(`Error en la subida: ${xhr.status} ${xhr.statusText}`));
      }
    });

    xhr.addEventListener("error", () => {
      reject(new Error("Error de red al subir la imagen"));
    });

    xhr.addEventListener("abort", () => {
      reject(new Error("Subida cancelada"));
    });

    xhr.open(
      "POST",
      `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`,
    );
    xhr.send(formData);
  });
};

export const uploadMultipleImagesToCloudinary = async (
  files: File[],
  onProgress?: (fileIndex: number, progress: UploadProgress) => void,
): Promise<CloudinaryResponse[]> => {
  const uploadPromises = files.map((file, index) =>
    uploadImageToCloudinary(file, (progress) => onProgress?.(index, progress)),
  );
  return Promise.all(uploadPromises);
};

export const deleteImageFromCloudinary = async (publicId: string): Promise<void> => {
  if (!CLOUDINARY_CLOUD_NAME) throw new Error("Configuración de Cloudinary incompleta");
  const response = await fetch(
    `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/destroy`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ public_id: publicId }),
    },
  );
  if (!response.ok) throw new Error("Error al eliminar la imagen de Cloudinary");
};
